// levels/rites.json: the 27 rites of the lattice, each with its deal, par and
// proof. Read once at start; nothing downstream checks it again.
//
// The file's own shapes: a card is its rings inner ring first, with null for
// a folded ring; a move names places as { t: "col", i, n } or { t: "altar", i }.

import * as z from "zod/mini"
import type { Deal, From, Move, To } from "../domain/engine.ts"
import { err, ok, type Result } from "../domain/result.ts"
import { ALL_RITE_IDS, proofHolds, type LatticeRite, type RiteId } from "../domain/rite.ts"
import type { Ring, Rings, Trit, Width } from "../domain/rings.ts"

const ringOf = (r: -1 | 0 | 1 | null): Ring => (r === null ? "folded" : r)
const RING = z.pipe(z.union([z.literal(-1), z.literal(0), z.literal(1), z.null()]), z.transform(ringOf))
const RINGS = z.union([z.tuple([RING]), z.tuple([RING, RING]), z.tuple([RING, RING, RING])])
const INDEX = z.int().check(z.minimum(0))

export const CardJson = z.strictObject({ t: RINGS })
export const ColumnJson = z.strictObject({ mirror: z.optional(z.boolean()), tide: z.optional(z.boolean()), cards: z.array(CardJson) })
export const DealJson = z.strictObject({
  width: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  altars: INDEX,
  cols: z.array(ColumnJson).check(z.minLength(1)),
})

const FROM = z.union([z.strictObject({ t: z.literal("col"), i: INDEX, n: z.int().check(z.minimum(1)) }), z.strictObject({ t: z.literal("altar"), i: INDEX })])
const TO = z.strictObject({ t: z.union([z.literal("col"), z.literal("altar")]), i: INDEX })
export const MoveJson = z.strictObject({ from: FROM, to: TO })

const RITE_ID = z.string().check(z.regex(/^[-0+]{3}$/))
const RITE = z.strictObject({ id: RITE_ID, deal: DealJson, par: INDEX, proof: z.array(MoveJson), score: z.number() })
const FILE = z.strictObject({ version: z.literal(1), rites: z.array(RITE) })

// What went wrong, in words a person can follow, with where.
export const problemText = (error: z.core.$ZodError): string => z.prettifyError(error)

export type MoveJsonShape = z.infer<typeof MoveJson>
export type DealJsonShape = z.infer<typeof DealJson>

export const moveOf = (m: MoveJsonShape): Move => {
  const from: From = m.from.t === "col" ? { kind: "col", i: m.from.i, n: m.from.n } : { kind: "altar", i: m.from.i }
  const to: To = { kind: m.to.t, i: m.to.i }
  return { from, to }
}

export const moveJsonOf = (m: Move): MoveJsonShape => ({
  from: m.from.kind === "col" ? { t: "col", i: m.from.i, n: m.from.n } : { t: "altar", i: m.from.i },
  to: { t: m.to.kind, i: m.to.i },
})

const cardsOf = (c: z.infer<typeof ColumnJson>): readonly Rings[] => c.cards.map((k) => k.t)

export const dealOf = (d: DealJsonShape): Deal => ({
  width: d.width,
  altars: d.altars,
  cols: d.cols.map((c) => ({ mirror: c.mirror ?? false, tide: c.tide ?? false, cards: cardsOf(c) })),
})

// The file's spelling of a deal: folded rings written as null.
export type DealFileShape = {
  readonly width: Width
  readonly altars: number
  readonly cols: readonly {
    readonly mirror?: true
    readonly tide?: true
    readonly cards: readonly { readonly t: readonly (Trit | null)[] }[]
  }[]
}

const fileRing = (r: Ring): Trit | null => (r === "folded" ? null : r)
const fileCard = (rings: Rings): { readonly t: readonly (Trit | null)[] } => ({ t: rings.map(fileRing) })

export const dealJsonOf = (d: Deal): DealFileShape => ({
  width: d.width,
  altars: d.altars,
  cols: d.cols.map((c) => ({ ...(c.mirror ? { mirror: true } : {}), ...(c.tide ? { tide: true } : {}), cards: c.cards.map(fileCard) })),
})

const ringsFit = (d: Deal): boolean => d.cols.every((c) => c.cards.every((r) => r.length === d.width))

// The id was checked by the regex above; this is the one place it is named a RiteId.
const asRiteId = (id: string): RiteId => id as RiteId

export type RitesProblem = { readonly tag: "malformed"; readonly detail: string } | { readonly tag: "invalid"; readonly detail: string }

export const parseRites = (input: unknown): Result<RitesProblem, readonly LatticeRite[]> => {
  const parsed = FILE.safeParse(input)
  if (!parsed.success) return err({ tag: "malformed", detail: problemText(parsed.error) })
  const rites = parsed.data.rites.map(
    (r): LatticeRite => ({ kind: "lattice", id: asRiteId(r.id), deal: dealOf(r.deal), par: r.par, proof: r.proof.map(moveOf) }),
  )
  const ids = new Set(rites.map((r) => r.id))
  if (ids.size !== ALL_RITE_IDS.length || rites.length !== ALL_RITE_IDS.length) {
    return err({ tag: "invalid", detail: "the lattice needs each of the 27 rites exactly once" })
  }
  const misfit = rites.find((r) => !ringsFit(r.deal))
  if (misfit !== undefined) return err({ tag: "invalid", detail: `rite ${misfit.id}: a card's rings do not match the deal's width` })
  const broken = rites.find((r) => !proofHolds(r))
  if (broken !== undefined) return err({ tag: "invalid", detail: `rite ${broken.id}: its proof does not reach the Monad in par` })
  return ok(rites)
}

// ---------- writing the file (scripts/rites.ts) ----------

// One rite in the file's own spelling.
export type RiteJson = {
  readonly id: RiteId
  readonly deal: DealFileShape
  readonly par: number
  readonly proof: readonly MoveJsonShape[]
  readonly score: number
}

export const riteJsonOf = (id: RiteId, deal: Deal, proof: readonly Move[], score: number): RiteJson => ({
  id,
  deal: dealJsonOf(deal),
  par: proof.length,
  proof: proof.map(moveJsonOf),
  score,
})

export type RitesFile = { readonly version: 1; readonly rites: readonly unknown[] }

export const ritesFileOf = (rites: readonly RiteJson[]): RitesFile => ({ version: 1, rites })

// The file as written, each rite left exactly as it is except the ids read.
const LOOSE_FILE = z.strictObject({ version: z.literal(1), rites: z.array(z.looseObject({ id: RITE_ID })) })

// The file with one rite swapped for a new one; every other rite stays as written.
export const withRite = (input: unknown, rite: RiteJson): Result<RitesProblem, RitesFile> => {
  const parsed = LOOSE_FILE.safeParse(input)
  if (!parsed.success) return err({ tag: "malformed", detail: problemText(parsed.error) })
  if (!parsed.data.rites.some((r) => r.id === rite.id)) return err({ tag: "invalid", detail: `the file has no rite ${rite.id}` })
  return ok({ version: 1, rites: parsed.data.rites.map((r): unknown => (r.id === rite.id ? rite : r)) })
}

// A deal typed in by hand (scripts/tutor-check.ts --deal), in the file's spelling.
export const parseDeal = (input: unknown): Result<RitesProblem, Deal> => {
  const parsed = DealJson.safeParse(input)
  if (!parsed.success) return err({ tag: "malformed", detail: problemText(parsed.error) })
  const deal = dealOf(parsed.data)
  return ringsFit(deal) ? ok(deal) : err({ tag: "invalid", detail: "a card's rings do not match the deal's width" })
}
